import { useMemo, useState, useEffect } from 'react'
import ApplicationProfiles from '../components/ApplicationProfiles'
import { Link, useParams } from 'react-router-dom'
import { ChevronLeft, Plus, X, History, ChevronDown, ChevronUp } from 'lucide-react'
import type { Asset, AssetSeverity, ScanStatus } from '../types'
import { Status, PublishedStatus, HistoryAction } from '../types'
import type { Vulnerability } from '../services/vulnerabilitiesApi'
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Cell,
  LabelList,
} from 'recharts'
import { useVulnerabilitiesStore } from '../stores/vulnerabilitiesStore'
import { useAuthStore } from '../stores/authStore'
import { vulnerabilitiesApi } from '../services/vulnerabilitiesApi'
import { scansApi } from '../services/scansApi'

const SEVERITY_COLORS: Record<AssetSeverity, string> = {
  Critical: '#C00000',
  High: '#FF0000',
  Medium: '#FFC000',
  Low: '#00B050',
  Info: '#00B0F0',
}

const statusBadgeClass: Record<ScanStatus, string> = {
  in_progress:
    'bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200',
  in_review:
    'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  completed:
    'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  on_hold:
    'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
}

const statusLabel: Record<ScanStatus, string> = {
  in_progress: 'In Progress',
  in_review: 'In Review',
  completed: 'Completed',
  on_hold: 'On Hold',
}

const ScanDetails = () => {
  const { projectId = '', assetId = '', scanId = '' } = useParams()

  // Store hooks
  const {
    loading,
    error,
    fetchVulnerabilitiesByScanId,
    createVulnerability,
    updateVulnerability,
    fixVulnerability,
    markReadyToRevalidate,
    setCurrentContext,
  } = useVulnerabilitiesStore()
  const { user, getUserRole } = useAuthStore()
  const userRole = getUserRole()

  // Scan state
  const [scan, setScan] = useState<any>(null)

  // Real vulnerabilities state
  const [realVulns, setRealVulns] = useState<any[]>([])
  const [vulnsLoading, setVulnsLoading] = useState(false)
  const [statsData, setStatsData] = useState<
    Array<{ name: AssetSeverity; value: number }>
  >([])

  const counts = useMemo(() => {
    // Use real stats data if available, otherwise show all severities with 0 values
    if (statsData.length > 0) {
      return statsData
    }
    return [
      { name: 'Critical' as AssetSeverity, value: 0 },
      { name: 'High' as AssetSeverity, value: 0 },
      { name: 'Medium' as AssetSeverity, value: 0 },
      { name: 'Low' as AssetSeverity, value: 0 },
      { name: 'Info' as AssetSeverity, value: 0 },
    ]
  }, [statsData])

  const vulns = useMemo(() => realVulns, [realVulns])
  const totalVulns = useMemo(
    () => counts.reduce((s, i) => s + i.value, 0),
    [counts]
  )
  const isDark =
    typeof document !== 'undefined' &&
    document.documentElement.classList.contains('dark')

  const [isVulnModalOpen, setIsVulnModalOpen] = useState(false)
  const [activeVuln, setActiveVuln] = useState<Vulnerability | null>(null)
  const [vulnTab, setVulnTab] = useState<'general' | 'pos'>('general')
  const [expandedPocs, setExpandedPocs] = useState<number[]>([])
  const [presignedUrls, setPresignedUrls] = useState<Record<string, string>>({})

  // Create vulnerability modal state
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [createTab, setCreateTab] = useState<'general' | 'poc'>('general')
  const [formData, setFormData] = useState<{
    name: string
    cvss: number | ''
    sla: number | ''
    synopsis: string
    recommendation: string
    impact: string
  }>({
    name: '',
    cvss: '',
    sla: '',
    synopsis: '',
    recommendation: '',
    impact: '',
  })
  const [references, setReferences] = useState<string[]>([''])
  
  // POC state
  type PocType = 'TEXT' | 'IMAGE'
  interface TextPoc {
    type: 'TEXT'
    caption: string
    details: string
  }
  interface ImageItem {
    caption: string
    imageKey: string
    file?: File
  }
  interface ImagePoc {
    type: 'IMAGE'
    images: ImageItem[]
  }
  type Poc = TextPoc | ImagePoc
  
  const [pocs, setPocs] = useState<Poc[]>([])

  // Edit vulnerability modal state
  const [showEditModal, setShowEditModal] = useState(false)
  const [editTab, setEditTab] = useState<'general' | 'poc'>('general')
  const [editingVuln, setEditingVuln] = useState<Vulnerability | null>(null)
  const [editFormData, setEditFormData] = useState<{
    cvss: number | ''
    sla: number | ''
    recommendation: string
    impact: string
    status: string
  }>({
    cvss: '',
    sla: '',
    recommendation: '',
    impact: '',
    status: Status.NOT_FIXED,
  })
  const [editReferences, setEditReferences] = useState<string[]>([''])
  const [editPocs, setEditPocs] = useState<Poc[]>([])

  // Import vulnerability modal state
  const [showImportModal, setShowImportModal] = useState(false)
  const [availableVulnerabilities, setAvailableVulnerabilities] = useState<
    any[]
  >([])
  const [importLoading, setImportLoading] = useState(false)
  const [importSearchQuery, setImportSearchQuery] = useState('')
  const [importPage, setImportPage] = useState(1)
  const [importPageSize, setImportPageSize] = useState(10)

  // History modal state
  const [showHistoryModal, setShowHistoryModal] = useState(false)
  const [historyData, setHistoryData] = useState<any[]>([])
  const [historyLoading, setHistoryLoading] = useState(false)

  // CVSS severity function
  const getCvssSeverity = (cvss: number): { level: string; color: string; bgColor: string; textColor: string } => {
    if (cvss >= 9.0)
      return {
        level: 'CRITICAL',
        color: 'bg-[#C00000] text-white dark:bg-[#C00000] dark:text-white',
        bgColor: '#C00000',
        textColor: '#ffffff'
      }
    if (cvss >= 7.0)
      return {
        level: 'HIGH',
        color: 'bg-[#FF0000] text-white dark:bg-[#FF0000] dark:text-white',
        bgColor: '#FF0000',
        textColor: '#ffffff'
      }
    if (cvss >= 4.0)
      return {
        level: 'MEDIUM',
        color: 'bg-[#FFC000] text-gray-900 dark:bg-[#FFC000] dark:text-gray-900',
        bgColor: '#FFC000',
        textColor: '#111827'
      }
    if (cvss >= 0.1)
      return {
        level: 'LOW',
        color: 'bg-[#00B050] text-white dark:bg-[#00B050] dark:text-white',
        bgColor: '#00B050',
        textColor: '#ffffff'
      }
    return {
      level: 'INFO',
      color: 'bg-[#00B0F0] text-white dark:bg-[#00B0F0] dark:text-white',
      bgColor: '#00B0F0',
      textColor: '#ffffff'
    }
  }

  // Check if user can manage vulnerabilities
  const canManageVulnerabilities =
    user?.roles?.includes('ADMIN') || user?.roles?.includes('PENTESTER')

  // Check if user can fix vulnerabilities (platform admin only)
  const canFixVulnerabilities = user?.roles?.includes('ADMIN')

  // Check if user can mark ready to revalidate (tenant admin and tenant user)
  const canMarkReadyToRevalidate =
    user?.roles?.includes('TENANT_ADMIN') ||
    user?.roles?.includes('TENANT_USER')

  // Fetch scan details when component mounts
  useEffect(() => {
    const fetchScan = async () => {
      if (scanId) {
        try {
          const response = await scansApi.getScanById(scanId)
          if (response.success && response.data) {
            setScan(response.data)
          }
        } catch (error) {
          console.error('Failed to fetch scan details:', error)
        } finally {
          // finished fetching scan
        }
      }
    }

    fetchScan()
  }, [scanId])

  // Set current context on component mount
  useEffect(() => {
    if (projectId && assetId && scanId) {
      setCurrentContext(projectId, assetId, scanId)
    }
  }, [projectId, assetId, scanId, setCurrentContext])

  // Function to refresh vulnerabilities
  const refreshVulnerabilities = async () => {
    if (scanId) {
      setVulnsLoading(true)
      try {
        const vulnerabilities = await fetchVulnerabilitiesByScanId(scanId)
        setRealVulns(vulnerabilities)
      } catch (error) {
        console.error('Failed to fetch vulnerabilities:', error)
      } finally {
        setVulnsLoading(false)
      }
    }
  }

  // Function to fetch vulnerability statistics
  const fetchVulnerabilityStats = async () => {
    if (scanId) {
      try {
        const response = await vulnerabilitiesApi.getVulnerabilityStats(scanId)
        if (response.success && response.data) {
          // Map API response to chart data format
          const severityMap: Record<string, AssetSeverity> = {
            CRITICAL: 'Critical',
            HIGH: 'High',
            MEDIUM: 'Medium',
            LOW: 'Low',
            INFO: 'Info',
          }

          // Create a map with all severities initialized to 0
          const allSeverities: Record<AssetSeverity, number> = {
            Critical: 0,
            High: 0,
            Medium: 0,
            Low: 0,
            Info: 0,
          }

          // Update counts from API response
          response.data.forEach(item => {
            const severity = severityMap[item._id]
            if (severity) {
              allSeverities[severity] = item.count
            }
          })

          // Convert to array format for chart
          const chartData: Array<{ name: AssetSeverity; value: number }> = [
            { name: 'Critical', value: allSeverities['Critical'] },
            { name: 'High', value: allSeverities['High'] },
            { name: 'Medium', value: allSeverities['Medium'] },
            { name: 'Low', value: allSeverities['Low'] },
            { name: 'Info', value: allSeverities['Info'] },
          ]

          setStatsData(chartData)
        }
      } catch (error) {
        console.error('Failed to fetch vulnerability stats:', error)
      }
    }
  }

  // Handle fixing vulnerability
  const handleFixVulnerability = async (vulnerabilityId: string) => {
    try {
      const success = await fixVulnerability(vulnerabilityId)
      if (success) {
        // Refresh the vulnerabilities list to show updated status
        await refreshVulnerabilities()
      }
    } catch (error) {
      console.error('Failed to fix vulnerability:', error)
    }
  }

  // Handle marking vulnerability as ready to revalidate
  const handleMarkReadyToRevalidate = async (vulnerabilityId: string) => {
    try {
      const success = await markReadyToRevalidate(vulnerabilityId)
      if (success) {
        // Refresh the vulnerabilities list to show updated status
        await refreshVulnerabilities()
      }
    } catch (error) {
      console.error(
        'Failed to mark vulnerability as ready to revalidate:',
        error
      )
    }
  }

  // Fetch real vulnerabilities and stats on component mount
  useEffect(() => {
    refreshVulnerabilities()
    fetchVulnerabilityStats()
  }, [scanId, fetchVulnerabilitiesByScanId])

  const openVulnModal = (v: Vulnerability) => {
    setActiveVuln(v)
    setVulnTab('general')
    setIsVulnModalOpen(true)
    // Reset presigned URLs cache
    setPresignedUrls({})
  }
  const closeVulnModal = () => {
    setIsVulnModalOpen(false)
    setActiveVuln(null)
    setExpandedPocs([])
    setPresignedUrls({})
  }

  const togglePocAccordion = (index: number) => {
    setExpandedPocs(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    )
  }

  const fetchPresignedUrl = async (imageKey: string) => {
    // Check if we already have this URL cached
    if (presignedUrls[imageKey]) {
      return presignedUrls[imageKey]
    }

    try {
      const response = await vulnerabilitiesApi.getPresignedUrl(imageKey)
      if (response.success && response.data) {
        const url = response.data.presignedUrl
        setPresignedUrls(prev => ({ ...prev, [imageKey]: url }))
        return url
      }
    } catch (error) {
      console.error('Failed to fetch presigned URL:', error)
    }
    return null
  }

  // Prefetch all presigned URLs when vulnerability modal opens
  useEffect(() => {
    const prefetchAllPresignedUrls = async () => {
      if (!activeVuln?.pocs || !isVulnModalOpen) return

      // Collect all image keys from all IMAGE POCs
      const imageKeys: string[] = []
      for (const poc of activeVuln.pocs) {
        if (poc.type === 'IMAGE' && poc.images) {
          for (const image of poc.images) {
            if (image.imageKey && !presignedUrls[image.imageKey]) {
              imageKeys.push(image.imageKey)
            }
          }
        }
      }

      // Fetch all presigned URLs in parallel
      if (imageKeys.length > 0) {
        await Promise.all(imageKeys.map(key => fetchPresignedUrl(key)))
      }
    }

    prefetchAllPresignedUrls()
  }, [activeVuln, isVulnModalOpen])

  // Create vulnerability handlers
  const openCreateModal = () => {
    setFormData({
      name: '',
      cvss: 0,
      sla: 30,
      synopsis: '',
      recommendation: '',
      impact: '',
    })
    setReferences([''])
    setShowCreateModal(true)
  }

  const closeCreateModal = () => {
    setShowCreateModal(false)
    setCreateTab('general')
    setFormData({
      name: '',
      cvss: '',
      sla: '',
      synopsis: '',
      recommendation: '',
      impact: '',
    })
    setReferences([''])
    setPocs([])
  }

  const handleCreateVulnerability = async (e: React.FormEvent) => {
    e.preventDefault()

    // Use the scan's tenant ID
    const tenantId = scan?.tenantId
    if (!tenantId) {
      console.error('Scan tenant ID is not available.')
      alert('Cannot create vulnerability: Scan tenant information is missing.')
      return
    }

    // Validate and convert numeric fields
    const cvss =
      typeof formData.cvss === 'number'
        ? formData.cvss
        : parseFloat(String(formData.cvss))
    const sla =
      typeof formData.sla === 'number'
        ? formData.sla
        : parseFloat(String(formData.sla))

    if (isNaN(cvss) || isNaN(sla)) {
      alert('Please enter valid numbers for CVSS and SLA')
      return
    }

    // Filter out empty references
    const filteredReferences = references.filter(ref => ref.trim() !== '')

    // Upload images for IMAGE POCs
    try {
      for (const poc of pocs) {
        if (poc.type === 'IMAGE' && poc.images.length > 0) {
          // Collect files that need to be uploaded
          const filesToUpload = poc.images
            .filter(img => img.file)
            .map(img => img.file!)
          
          if (filesToUpload.length > 0) {
            // Upload all files at once
            const uploadResponse = await vulnerabilitiesApi.uploadImages(tenantId, filesToUpload)
            
            if (uploadResponse.success && uploadResponse.data) {
              // Replace placeholder imageKeys with actual s3Keys
              let uploadIndex = 0
              for (const image of poc.images) {
                if (image.file) {
                  image.imageKey = uploadResponse.data.images[uploadIndex].s3Key
                  delete image.file // Remove file object
                  uploadIndex++
                }
              }
            }
          }
        }
      }
    } catch (uploadError) {
      console.error('Failed to upload images:', uploadError)
      alert('Failed to upload images. Please try again.')
      return
    }

    // Prepare POCs for API
    const preparedPocs = pocs.map(poc => {
      if (poc.type === 'TEXT') {
        return {
          type: 'TEXT' as const,
          caption: poc.caption,
          details: poc.details
        }
      } else {
        return {
          type: 'IMAGE' as const,
          images: poc.images.filter(img => img.imageKey && img.imageKey.trim() !== '')
        }
      }
    }).filter(poc => {
      // Filter out empty POCs
      if (poc.type === 'TEXT') {
        return poc.caption.trim() !== '' || (poc.details?.trim() || '') !== ''
      } else {
        return (poc.images?.length || 0) > 0
      }
    })

    // Create the vulnerability for this scan
    const success = await createVulnerability({
      tenantId: tenantId,
      projectId,
      assetId,
      scanId,
      name: formData.name,
      cvss,
      sla,
      synopsis: formData.synopsis,
      recommendation: formData.recommendation,
      impact: formData.impact,
      reference: filteredReferences.length > 0 ? filteredReferences : undefined,
      pocs: preparedPocs.length > 0 ? preparedPocs : undefined,
    } as any)

    if (success) {
      closeCreateModal()
      // Refresh the vulnerabilities list and stats to show the new vulnerability
      await refreshVulnerabilities()
      await fetchVulnerabilityStats()
    }
  }

  // Edit vulnerability handlers
  const openEditModal = (vuln: Vulnerability) => {
    setEditingVuln(vuln)
    setEditFormData({
      cvss: vuln.cvss || 0,
      sla: vuln.sla || 30,
      recommendation: vuln.recommendation || '',
      impact: vuln.impact || '',
      status: vuln.status || Status.NOT_FIXED,
    })
    setEditReferences(
      vuln.reference && vuln.reference.length > 0 ? vuln.reference : ['']
    )
    // Initialize POCs from vulnerability data
    if (vuln.pocs && vuln.pocs.length > 0) {
      setEditPocs(vuln.pocs.map(poc => {
        if (poc.type === 'TEXT') {
          return {
            type: 'TEXT' as const,
            caption: poc.caption || '',
            details: poc.details || ''
          }
        } else {
          return {
            type: 'IMAGE' as const,
            images: poc.images || []
          }
        }
      }))
    } else {
      setEditPocs([])
    }
    setShowEditModal(true)
  }

  const closeEditModal = () => {
    setShowEditModal(false)
    setEditTab('general')
    setEditingVuln(null)
    setEditFormData({
      cvss: '',
      sla: '',
      recommendation: '',
      impact: '',
      status: Status.NOT_FIXED,
    })
    setEditReferences([''])
    setEditPocs([])
  }

  const handleUpdateVulnerability = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!editingVuln) return

    // Validate and convert numeric fields
    const cvss =
      typeof editFormData.cvss === 'number'
        ? editFormData.cvss
        : parseFloat(String(editFormData.cvss))
    const sla =
      typeof editFormData.sla === 'number'
        ? editFormData.sla
        : parseFloat(String(editFormData.sla))

    if (isNaN(cvss) || isNaN(sla)) {
      alert('Please enter valid numbers for CVSS and SLA')
      return
    }

    // Filter out empty references
    const filteredReferences = editReferences.filter(ref => ref.trim() !== '')

    // Get tenantId for image upload
    const tenantId = editingVuln.tenantId
    if (!tenantId) {
      console.error('Tenant ID is not available.')
      alert('Cannot update vulnerability: Tenant information is missing.')
      return
    }

    // Upload images for IMAGE POCs
    try {
      for (const poc of editPocs) {
        if (poc.type === 'IMAGE' && poc.images.length > 0) {
          // Collect files that need to be uploaded
          const filesToUpload = poc.images
            .filter(img => img.file)
            .map(img => img.file!)
          
          if (filesToUpload.length > 0) {
            // Upload all files at once
            const uploadResponse = await vulnerabilitiesApi.uploadImages(tenantId, filesToUpload)
            
            if (uploadResponse.success && uploadResponse.data) {
              // Replace placeholder imageKeys with actual s3Keys
              let uploadIndex = 0
              for (const image of poc.images) {
                if (image.file) {
                  image.imageKey = uploadResponse.data.images[uploadIndex].s3Key
                  delete image.file // Remove file object
                  uploadIndex++
                }
              }
            }
          }
        }
      }
    } catch (uploadError) {
      console.error('Failed to upload images:', uploadError)
      alert('Failed to upload images. Please try again.')
      return
    }

    // Prepare POCs for API
    const preparedEditPocs = editPocs.map(poc => {
      if (poc.type === 'TEXT') {
        return {
          type: 'TEXT' as const,
          caption: poc.caption,
          details: poc.details
        }
      } else {
        return {
          type: 'IMAGE' as const,
          images: poc.images.filter(img => img.imageKey && img.imageKey.trim() !== '')
        }
      }
    }).filter(poc => {
      // Filter out empty POCs
      if (poc.type === 'TEXT') {
        return poc.caption.trim() !== '' || (poc.details?.trim() || '') !== ''
      } else {
        return (poc.images?.length || 0) > 0
      }
    })

    // Update the vulnerability
    const success = await updateVulnerability(editingVuln._id, {
      cvss,
      sla,
      recommendation: editFormData.recommendation,
      impact: editFormData.impact,
      status: editFormData.status,
      reference: filteredReferences.length > 0 ? filteredReferences : undefined,
      pocs: preparedEditPocs.length > 0 ? preparedEditPocs : undefined,
    } as any)

    if (success) {
      closeEditModal()
      // Refresh the vulnerabilities list to show updated data
      await refreshVulnerabilities()
      await fetchVulnerabilityStats()
    }
  }

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target
    setFormData(prev => ({
      ...prev,
      [name]:
        type === 'checkbox'
          ? (e.target as HTMLInputElement).checked
          : name === 'cvss' || name === 'sla'
            ? value === ''
              ? ''
              : parseFloat(value)
            : value,
    }))
  }

  // Reference management functions
  const addReference = () => {
    setReferences(prev => [...prev, ''])
  }

  const removeReference = (index: number) => {
    setReferences(prev => prev.filter((_, i) => i !== index))
  }

  const updateReference = (index: number, value: string) => {
    setReferences(prev => prev.map((ref, i) => (i === index ? value : ref)))
  }

  // POC management functions
  const addPoc = (type: PocType) => {
    if (type === 'TEXT') {
      setPocs(prev => [...prev, { type: 'TEXT', caption: '', details: '' }])
    } else {
      setPocs(prev => [...prev, { type: 'IMAGE', images: [] }])
    }
  }

  const removePoc = (index: number) => {
    setPocs(prev => prev.filter((_, i) => i !== index))
  }

  const updatePocCaption = (index: number, caption: string) => {
    setPocs(prev => prev.map((poc, i) => (i === index ? { ...poc, caption } : poc)))
  }

  const updateTextPocDetails = (index: number, details: string) => {
    setPocs(prev => prev.map((poc, i) => {
      if (i === index && poc.type === 'TEXT') {
        return { ...poc, details }
      }
      return poc
    }))
  }

  const addImageToPoc = (pocIndex: number) => {
    setPocs(prev => prev.map((poc, i) => {
      if (i === pocIndex && poc.type === 'IMAGE') {
        return { ...poc, images: [...poc.images, { caption: '', imageKey: '' }] }
      }
      return poc
    }))
  }

  const removeImageFromPoc = (pocIndex: number, imageIndex: number) => {
    setPocs(prev => prev.map((poc, i) => {
      if (i === pocIndex && poc.type === 'IMAGE') {
        return { ...poc, images: poc.images.filter((_, j) => j !== imageIndex) }
      }
      return poc
    }))
  }

  const updateImageCaption = (pocIndex: number, imageIndex: number, caption: string) => {
    setPocs(prev => prev.map((poc, i) => {
      if (i === pocIndex && poc.type === 'IMAGE') {
        return {
          ...poc,
          images: poc.images.map((img, j) => (j === imageIndex ? { ...img, caption } : img))
        }
      }
      return poc
    }))
  }

  const updateImageFile = (pocIndex: number, imageIndex: number, file: File) => {
    setPocs(prev => prev.map((poc, i) => {
      if (i === pocIndex && poc.type === 'IMAGE') {
        return {
          ...poc,
          images: poc.images.map((img, j) => 
            j === imageIndex ? { ...img, file, imageKey: `placeholder_${file.name}` } : img
          )
        }
      }
      return poc
    }))
  }

  // Edit POC management functions
  const addEditPoc = (type: PocType) => {
    if (type === 'TEXT') {
      setEditPocs(prev => [...prev, { type: 'TEXT', caption: '', details: '' }])
    } else {
      setEditPocs(prev => [...prev, { type: 'IMAGE', images: [] }])
    }
  }

  const removeEditPoc = (index: number) => {
    setEditPocs(prev => prev.filter((_, i) => i !== index))
  }

  const updateEditPocCaption = (index: number, caption: string) => {
    setEditPocs(prev => prev.map((poc, i) => (i === index ? { ...poc, caption } : poc)))
  }

  const updateEditTextPocDetails = (index: number, details: string) => {
    setEditPocs(prev => prev.map((poc, i) => {
      if (i === index && poc.type === 'TEXT') {
        return { ...poc, details }
      }
      return poc
    }))
  }

  const addImageToEditPoc = (pocIndex: number) => {
    setEditPocs(prev => prev.map((poc, i) => {
      if (i === pocIndex && poc.type === 'IMAGE') {
        return { ...poc, images: [...poc.images, { caption: '', imageKey: '' }] }
      }
      return poc
    }))
  }

  const removeImageFromEditPoc = (pocIndex: number, imageIndex: number) => {
    setEditPocs(prev => prev.map((poc, i) => {
      if (i === pocIndex && poc.type === 'IMAGE') {
        return { ...poc, images: poc.images.filter((_, j) => j !== imageIndex) }
      }
      return poc
    }))
  }

  const updateEditImageCaption = (pocIndex: number, imageIndex: number, caption: string) => {
    setEditPocs(prev => prev.map((poc, i) => {
      if (i === pocIndex && poc.type === 'IMAGE') {
        return {
          ...poc,
          images: poc.images.map((img, j) => (j === imageIndex ? { ...img, caption } : img))
        }
      }
      return poc
    }))
  }

  const updateEditImageFile = (pocIndex: number, imageIndex: number, file: File) => {
    setEditPocs(prev => prev.map((poc, i) => {
      if (i === pocIndex && poc.type === 'IMAGE') {
        return {
          ...poc,
          images: poc.images.map((img, j) => 
            j === imageIndex ? { ...img, file, imageKey: `placeholder_${file.name}` } : img
          )
        }
      }
      return poc
    }))
  }

  // Import vulnerability handlers
  const openImportModal = async () => {
    setShowImportModal(true)
    setImportLoading(true)
    setImportSearchQuery('')
    setImportPage(1) // Reset to first page
    setImportPageSize(10) // Reset to default page size

    try {
      // Fetch saved vulnerabilities from the database collection
      const response = await vulnerabilitiesApi.getSavedVulnerabilities({
        page: 1,
        limit: 100,
      })
      if (response.success && response.data) {
        setAvailableVulnerabilities(response.data)
      }
    } catch (error) {
      console.error('Failed to fetch saved vulnerabilities:', error)
    } finally {
      setImportLoading(false)
    }
  }

  const closeImportModal = () => {
    setShowImportModal(false)
    setAvailableVulnerabilities([])
    setImportSearchQuery('')
  }

  const handleImportSearch = async (searchQuery: string) => {
    setImportSearchQuery(searchQuery)
    setImportPage(1) // Reset to first page on search
    setImportLoading(true)

    try {
      const response = await vulnerabilitiesApi.getSavedVulnerabilities({
        page: 1,
        limit: 100,
        search: searchQuery || undefined,
      })
      if (response.success && response.data) {
        setAvailableVulnerabilities(response.data)
      }
    } catch (error) {
      console.error('Failed to search saved vulnerabilities:', error)
    } finally {
      setImportLoading(false)
    }
  }

  const handleImportVulnerability = async (vulnerability: any) => {
    // Auto-fill the form with the selected vulnerability data
    setFormData({
      name: vulnerability.name,
      cvss: vulnerability.cvss,
      sla: vulnerability.sla,
      synopsis: vulnerability.synopsis,
      recommendation: vulnerability.recommendation || '', // Recommendation might not exist in saved vulns
      impact: vulnerability.impact,
    })

    // Set references if available, otherwise start with empty array
    setReferences(
      vulnerability.reference && vulnerability.reference.length > 0
        ? vulnerability.reference
        : ['']
    )

    // Close import modal and open create modal
    closeImportModal()
    setShowCreateModal(true)
  }

  // History handlers
  const openHistoryModal = async () => {
    setShowHistoryModal(true)
    setHistoryLoading(true)

    try {
      const response = await vulnerabilitiesApi.getScanHistory(scanId)
      if (response.success && response.data) {
        // The API response has data.data structure
        const historyItems = (response.data as any).data || []
        setHistoryData(historyItems)
      }
    } catch (error) {
      console.error('Failed to fetch scan history:', error)
    } finally {
      setHistoryLoading(false)
    }
  }

  const closeHistoryModal = () => {
    setShowHistoryModal(false)
    setHistoryData([])
  }

  const getActionLabel = (action: string) => {
    const actionLabels: Record<string, string> = {
      [HistoryAction.SCAN_INITIATED]: 'Scan Initiated',
      [HistoryAction.CREATE]: 'Vulnerability Created',
      [HistoryAction.STATUS_CHANGE]: 'Status Changed',
      [HistoryAction.PUBLISH_STATUS_CHANGE]: 'Publish Status Changed',
      'UPDATE': 'Vulnerability Updated',
      'DELETE': 'Vulnerability Deleted',
      [Status.FIXED]: 'Marked as Fixed',
      [Status.READY_TO_REVALIDATE]: 'Ready to Revalidate',
    }
    return actionLabels[action] || action
  }

  const formatHistoryDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const CustomTooltip = ({
    active,
    payload,
  }: {
    active?: boolean
    payload?: any[]
  }) => {
    if (!active || !payload || payload.length === 0) return null
    const item = payload[0]
    const name = item?.payload?.name as AssetSeverity
    const value = item?.value as number
    const color = SEVERITY_COLORS[name]
    return (
      <div className="rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 shadow-md dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100">
        <div className="flex items-center gap-2">
          <span
            className="inline-block h-2.5 w-2.5 rounded-sm"
            style={{ backgroundColor: color }}
          />
          <span className="font-medium">{name}</span>
          <span className="ml-2 text-gray-500 dark:text-gray-400">{value}</span>
        </div>
      </div>
    )
  }

  const scanName = useMemo(() => scan?.name || `Security Scan ${scanId.slice(-4)}`, [scan, scanId])
  const assetName = useMemo(() => scan?.asset?.name || `Asset ${assetId.slice(-4)}`, [scan, assetId])
  const assetType = useMemo<Asset['type']>(() => {
    if (scan?.asset?.assetType) {
      const typeMap: Record<string, Asset['type']> = {
        'WEB_APPLICATION': 'Web App',
        'API': 'API',
        'MOBILE_APPLICATION': 'Mobile App',
        'NETWORK': 'Network',
        'CLOUD': 'Cloud',
        'OTHER': 'Other'
      }
      return typeMap[scan.asset.assetType] || 'Other'
    }
    return 'Web App'
  }, [scan])
  const createdOn = useMemo(() => {
    if (scan?.createdAt) {
      const d = new Date(scan.createdAt)
      return d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    }
    return 'N/A'
  }, [scan])
  const updatedOn = useMemo(() => {
    if (scan?.updatedAt) {
      const d = new Date(scan.updatedAt)
      return d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    }
    return 'N/A'
  }, [scan])

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Link
          to={`/projects/${projectId}/assets/${assetId}`}
          className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
        >
          <ChevronLeft className="h-4 w-4 mr-1" /> Back to Asset
        </Link>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          {scanName}
        </h1>
        <div />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-6 gap-6">
        {/* Left: Chart (smaller) */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 lg:col-span-2">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Total Vulnerabilities
            <span className="ml-2 align-middle inline-flex items-center px-2 py-0.5 rounded text-sm font-medium text-primary-700 dark:text-primary-300">
              {totalVulns}
            </span>
          </h2>
          <div className="h-64 flex items-center justify-center">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={counts}
                margin={{ top: 20, right: 20, left: 0, bottom: 0 }}
              >
                <XAxis
                  dataKey="name"
                  tick={{ fill: isDark ? '#d1d5db' : '#6b7280' }}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fill: isDark ? '#d1d5db' : '#6b7280' }}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip
                  cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                  content={<CustomTooltip />}
                />
                <Legend
                  wrapperStyle={{ color: isDark ? '#d1d5db' : '#6b7280' }}
                />
                <Bar dataKey="value" name="Count" barSize={20}>
                  {counts.map(entry => (
                    <Cell
                      key={`cell-${entry.name}`}
                      fill={SEVERITY_COLORS[entry.name]}
                    />
                  ))}
                  <LabelList
                    dataKey="value"
                    position="top"
                    content={(props: any) => {
                      const { x, y, width, value } = props
                      const cx = x + (width || 0) / 2
                      const isDark =
                        document.documentElement.classList.contains('dark')
                      const fill = isDark ? '#93c5fd' : '#2563eb'
                      return (
                        <text
                          x={cx}
                          y={(y || 0) - 6}
                          textAnchor="middle"
                          fill={fill}
                          fontSize={12}
                          fontWeight={600}
                        >
                          {value}
                        </text>
                      )
                    }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Middle: Scan details (wider) */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Scan Details
            </h2>
            {(userRole === 'ADMIN' || userRole === 'PENTESTER') && (
              <button
                onClick={openHistoryModal}
                className="p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700 transition-colors"
                title="View History"
              >
                <History className="h-5 w-5" />
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
            <div>
              <div className="text-gray-500 dark:text-gray-400">Name</div>
              <div className="text-gray-900 dark:text-white">{scanName}</div>
            </div>
            <div>
              <div className="text-gray-500 dark:text-gray-400">Asset Name</div>
              <div className="text-gray-900 dark:text-white">{assetName}</div>
            </div>
            <div>
              <div className="text-gray-500 dark:text-gray-400">Asset Type</div>
              <div className="text-gray-900 dark:text-white">{assetType}</div>
            </div>
            <div>
              <div className="text-gray-500 dark:text-gray-400">Scan Type</div>
              <div className="text-gray-900 dark:text-white">
                {scan?.type || 'Manual'}
              </div>
            </div>
            <div>
              <div className="text-gray-500 dark:text-gray-400">Testing Type</div>
              <div className="text-gray-900 dark:text-white">
                {scan?.testingType ? scan.testingType.replace(/_/g, ' ') : 'Black Box'}
              </div>
            </div>
            <div>
              <div className="text-gray-500 dark:text-gray-400">Status</div>
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  scan?.status 
                    ? statusBadgeClass[scan.status.toLowerCase() as ScanStatus] || statusBadgeClass['in_progress']
                    : statusBadgeClass['in_progress']
                }`}
              >
                {scan?.status 
                  ? statusLabel[scan.status.toLowerCase() as ScanStatus] || 'In Progress'
                  : 'In Progress'}
              </span>
            </div>
            <div>
              <div className="text-gray-500 dark:text-gray-400">Created On</div>
              <div className="text-gray-900 dark:text-white">{createdOn}</div>
            </div>
            <div>
              <div className="text-gray-500 dark:text-gray-400">Updated On</div>
              <div className="text-gray-900 dark:text-white">{updatedOn}</div>
            </div>
            {scan?.description && (
              <div className="col-span-full">
                <div className="text-gray-500 dark:text-gray-400">Description</div>
                <div className="text-gray-900 dark:text-white">{scan.description}</div>
              </div>
            )}
          </div>
        </div>
        {/* Right: Application Profiles box (wider) */}
        <div className="lg:col-span-2">
          <ApplicationProfiles
            className="bg-white dark:bg-gray-800 shadow rounded-lg p-6"
            scanId={scanId}
            tenantId={scan?.tenantId || ''}
            projectId={projectId}
            assetId={assetId}
          />
        </div>
      </div>

      {/* Vulnerabilities table */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Vulnerabilities
          </h2>
          <div className="flex items-center gap-4">
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {vulns.length} total
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={refreshVulnerabilities}
                disabled={vulnsLoading}
                className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 text-sm leading-4 font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg
                  className={`h-4 w-4 mr-2 ${vulnsLoading ? 'animate-spin' : ''
                    }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                Refresh
              </button>
              {canManageVulnerabilities && (
                <>
                  <button
                    onClick={openImportModal}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 text-sm leading-4 font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Import Vulnerability
                  </button>
                  <button
                    onClick={openCreateModal}
                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Vulnerability
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Title
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Type of Scan
                </th>
                {canManageVulnerabilities && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Created By
                  </th>
                )}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Severity
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  CVSS
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Status
                </th>
                {canManageVulnerabilities && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Published Status
                  </th>
                )}
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {vulnsLoading ? (
                <tr>
                  <td
                    colSpan={canManageVulnerabilities ? 8 : 6}
                    className="px-6 py-8 text-center text-gray-500 dark:text-gray-400"
                  >
                    Loading vulnerabilities...
                  </td>
                </tr>
              ) : vulns.length === 0 ? (
                <tr>
                  <td
                    colSpan={canManageVulnerabilities ? 8 : 6}
                    className="px-6 py-8 text-center text-gray-500 dark:text-gray-400"
                  >
                    No vulnerabilities found for this scan.
                  </td>
                </tr>
              ) : (
                vulns.map(v => {
                  const createdBy =
                    typeof v.createdBy === 'object' && v.createdBy
                      ? v.createdBy
                      : null
                  const initials = createdBy
                    ? `${createdBy.firstName.charAt(
                      0
                    )}${createdBy.lastName.charAt(0)}`.toUpperCase()
                    : 'NA'
                  const fullName = createdBy
                    ? `${createdBy.firstName} ${createdBy.lastName}`
                    : 'Unknown'

                  return (
                    <tr
                      key={v._id || v.id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                        {v.name || v.title}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                        {v.scanType || 'Manual'}
                      </td>
                      {canManageVulnerabilities && (
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="relative group">
                              <div className="h-8 w-8 bg-primary-600 text-white rounded-full flex items-center justify-center text-xs font-semibold cursor-pointer">
                                {initials}
                              </div>
                              {/* Custom tooltip */}
                              <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-3 py-1.5 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-md whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 pointer-events-none z-10">
                                {fullName}
                                {/* Tooltip arrow */}
                                <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-900 dark:border-t-gray-700"></div>
                              </div>
                            </div>
                          </div>
                        </td>
                      )}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                          style={{
                            backgroundColor: v.severity?.toUpperCase() === 'CRITICAL' || v.severity === 'Critical' ? '#C00000'
                              : v.severity?.toUpperCase() === 'HIGH' || v.severity === 'High' ? '#FF0000'
                              : v.severity?.toUpperCase() === 'MEDIUM' || v.severity === 'Medium' ? '#FFC000'
                              : v.severity?.toUpperCase() === 'LOW' || v.severity === 'Low' ? '#00B050'
                              : '#00B0F0',
                            color: (v.severity?.toUpperCase() === 'MEDIUM' || v.severity === 'Medium') ? '#111827' : '#ffffff'
                          }}
                        >
                          {v.severity}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                        {v.cvss ? v.cvss.toFixed(1) : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                        {(canFixVulnerabilities || canMarkReadyToRevalidate) &&
                          v.status === Status.NOT_FIXED ? (
                          <select
                            value={v.status}
                            onChange={e => {
                              if (
                                e.target.value === Status.FIXED &&
                                canFixVulnerabilities
                              ) {
                                handleFixVulnerability(v._id)
                              } else if (
                                e.target.value === Status.READY_TO_REVALIDATE &&
                                canMarkReadyToRevalidate
                              ) {
                                handleMarkReadyToRevalidate(v._id)
                              }
                            }}
                            className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          >
                            <option value={Status.NOT_FIXED}>Not Fixed</option>
                            {canFixVulnerabilities && (
                              <option value={Status.FIXED}>Mark as Fixed</option>
                            )}
                            {canMarkReadyToRevalidate && (
                              <option value={Status.READY_TO_REVALIDATE}>
                                Ready to Revalidate
                              </option>
                            )}
                          </select>
                        ) : (
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${v.status === Status.FIXED
                              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                              : v.status === Status.READY_TO_REVALIDATE
                                ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                                : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                              }`}
                          >
                            {v.status ? v.status.replace(/_/g, ' ') : 'Unknown'}
                          </span>
                        )}
                      </td>
                      {canManageVulnerabilities && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${v.publishedStatus === PublishedStatus.PUBLISHED
                              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                              : v.publishedStatus === PublishedStatus.UNPUBLISHED
                                ? 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                                : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                              }`}
                          >
                            {v.publishedStatus || PublishedStatus.UNPUBLISHED}
                          </span>
                        </td>
                      )}
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          {canManageVulnerabilities && (
                            <button
                              className="px-2 py-1 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                              onClick={() => openEditModal(v)}
                            >
                              Edit
                            </button>
                          )}
                          <button
                            className="px-2 py-1 text-gray-600 hover:text-primary-600 dark:text-gray-300 dark:hover:text-primary-400"
                            onClick={() => openVulnModal(v)}
                          >
                            View
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Vulnerability Modal */}
      {isVulnModalOpen && activeVuln && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={closeVulnModal}
          />
          <div className="relative z-10 w-full max-w-3xl mx-4 bg-white dark:bg-gray-800 rounded-lg shadow-lg">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {activeVuln.name || activeVuln.title}
              </h3>
              <button
                onClick={closeVulnModal}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                ✕
              </button>
            </div>
            {/* Tabs */}
            <div className="px-6 pt-4">
              <div className="border-b border-gray-200 dark:border-gray-700">
                <nav className="-mb-px flex space-x-6" aria-label="Tabs">
                  <button
                    onClick={() => setVulnTab('general')}
                    className={`whitespace-nowrap py-3 px-1 border-b-2 text-sm font-medium ${vulnTab === 'general'
                      ? 'border-primary-600 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                      }`}
                  >
                    General
                  </button>
                  <button
                    onClick={() => setVulnTab('pos')}
                    className={`whitespace-nowrap py-3 px-1 border-b-2 text-sm font-medium ${vulnTab === 'pos'
                      ? 'border-primary-600 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                      }`}
                  >
                    POC
                  </button>
                </nav>
              </div>
            </div>
            {/* Content */}
            <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
              {vulnTab === 'general' && (
                <div className="space-y-6 text-sm">
                  {/* Details */}
                  <div className="space-y-2">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <div className="text-gray-500 dark:text-gray-400">
                          Scan Type
                        </div>
                        <div className="text-gray-900 dark:text-white">
                          {activeVuln.scanType || 'Manual'}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-500 dark:text-gray-400">
                          Severity
                        </div>
                        <div className="text-gray-900 dark:text-white">
                          {activeVuln.severity}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-500 dark:text-gray-400">
                          CVSS
                        </div>
                        <div className="text-gray-900 dark:text-white">
                          {activeVuln.cvss ? activeVuln.cvss.toFixed(1) : 'N/A'}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-500 dark:text-gray-400">
                          Created On
                        </div>
                        <div className="text-gray-900 dark:text-white">
                          {activeVuln.createdOn ||
                            (activeVuln.createdAt
                              ? new Date(
                                activeVuln.createdAt
                              ).toLocaleDateString()
                              : 'Unknown')}
                        </div>
                      </div>
                      {canManageVulnerabilities &&
                        typeof activeVuln.createdBy === 'object' &&
                        activeVuln.createdBy && (
                          <div>
                            <div className="text-gray-500 dark:text-gray-400">
                              Created By
                            </div>
                            <div className="flex items-center space-x-2">
                              {/* <div className="h-6 w-6 bg-primary-600 text-white rounded-full flex items-center justify-center text-xs font-semibold">
                              {`${activeVuln.createdBy.firstName.charAt(0)}${activeVuln.createdBy.lastName.charAt(0)}`.toUpperCase()}
                            </div> */}
                              <div className="text-gray-900 dark:text-white">
                                {`${activeVuln.createdBy.firstName} ${activeVuln.createdBy.lastName}`}
                              </div>
                            </div>
                          </div>
                        )}
                      <div>
                        <div className="text-gray-500 dark:text-gray-400">
                          Status
                        </div>
                        <div className="text-gray-900 dark:text-white">
                          {activeVuln.status
                            ? activeVuln.status.replace('_', ' ')
                            : 'Unknown'}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="border-t border-gray-200 dark:border-gray-700" />
                  {/* Synopsis */}
                  <div className="space-y-1">
                    <div className="text-gray-900 dark:text-white font-medium">
                      Synopsis
                    </div>
                    <p className="text-gray-600 dark:text-gray-300">
                      {activeVuln.synopsis ||
                        `${activeVuln.name || activeVuln.title
                        } allows potential attackers to exploit improper validation resulting in unintended access or data exposure. Review configurations and apply recommended mitigations.`}
                    </p>
                  </div>
                  <div className="border-t border-gray-200 dark:border-gray-700" />
                  {/* Impact */}
                  <div className="space-y-1">
                    <div className="text-gray-900 dark:text-white font-medium">
                      Impact
                    </div>
                    <p className="text-gray-600 dark:text-gray-300">
                      {activeVuln.impact ||
                        'Successful exploitation may lead to privilege escalation, data leakage, or service disruption depending on environment and permissions.'}
                    </p>
                  </div>
                  <div className="border-t border-gray-200 dark:border-gray-700" />
                  {/* Recommendation */}
                  <div className="space-y-1">
                    <div className="text-gray-900 dark:text-white font-medium">
                      Recommendation
                    </div>
                    <p className="text-gray-600 dark:text-gray-300">
                      {activeVuln.recommendation ||
                        'Apply vendor patches, enforce input validation, sanitize outputs, and adopt least privilege. Add WAF rules where applicable.'}
                    </p>
                  </div>
                  <div className="border-t border-gray-200 dark:border-gray-700" />
                  {/* Reference */}
                  <div className="space-y-1">
                    <div className="text-gray-900 dark:text-white font-medium">
                      Reference
                    </div>
                    {activeVuln.reference && activeVuln.reference.length > 0 ? (
                      <ul className="list-disc pl-5 text-gray-600 dark:text-gray-300">
                        {activeVuln.reference.map((ref, index) => (
                          <li key={index}>
                            <a
                              href={ref}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300"
                            >
                              {ref}
                            </a>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <ul className="list-disc pl-5 text-gray-600 dark:text-gray-300">
                      </ul>
                    )}
                  </div>
                </div>
              )}
              {vulnTab === 'pos' && (
                <div className="space-y-3">
                  {activeVuln.pocs && activeVuln.pocs.length > 0 ? (
                    activeVuln.pocs.map((poc, pocIndex) => {
                      const isExpanded = expandedPocs.includes(pocIndex)
                      const pocCaption = poc.type === 'TEXT' 
                        ? (poc.caption || `Text POC ${pocIndex + 1}`)
                        : `Image POC ${pocIndex + 1}`

                      return (
                        <div key={poc._id || pocIndex} className="border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden bg-white dark:bg-gray-800">
                          {/* Accordion Header */}
                          <button
                            type="button"
                            onClick={() => togglePocAccordion(pocIndex)}
                            className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200">
                                {poc.type === 'TEXT' ? 'Text' : 'Image'}
                              </span>
                              <span className="text-sm font-medium text-gray-900 dark:text-white">
                                {pocCaption}
                              </span>
                            </div>
                            {isExpanded ? (
                              <ChevronUp className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                            ) : (
                              <ChevronDown className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                            )}
                          </button>

                          {/* Accordion Content */}
                          {isExpanded && (
                            <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                              {/* TEXT POC Details */}
                              {poc.type === 'TEXT' && poc.details && (
                                <div className="text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md p-3">
                                  <div 
                                    className="whitespace-pre-wrap break-words"
                                    dangerouslySetInnerHTML={{ __html: poc.details }}
                                  />
                                </div>
                              )}

                              {/* IMAGE POC */}
                              {poc.type === 'IMAGE' && poc.images && poc.images.length > 0 && (
                                <div className="space-y-3">
                                  {poc.images.map((image, imageIndex) => (
                                    <div key={imageIndex} className="border border-gray-300 dark:border-gray-600 rounded-md overflow-hidden bg-white dark:bg-gray-800">
                                      {image.caption && (
                                        <div className="px-3 py-2 bg-gray-100 dark:bg-gray-900 border-b border-gray-300 dark:border-gray-600">
                                          <div className="text-xs font-medium text-gray-600 dark:text-gray-400">
                                            {image.caption}
                                          </div>
                                        </div>
                                      )}
                                      {image.imageKey && (
                                        <div className="p-2">
                                          {presignedUrls[image.imageKey] ? (
                                            <img
                                              src={presignedUrls[image.imageKey]}
                                              alt={image.caption || `POC Image ${imageIndex + 1}`}
                                              className="w-full h-auto rounded"
                                              onError={(e) => {
                                                const target = e.target as HTMLImageElement;
                                                target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Crect width="400" height="300" fill="%23f3f4f6"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="14" fill="%239ca3af"%3EImage not available%3C/text%3E%3C/svg%3E';
                                              }}
                                            />
                                          ) : (
                                            <div className="flex items-center justify-center h-48 bg-gray-100 dark:bg-gray-900 rounded">
                                              <div className="text-sm text-gray-500 dark:text-gray-400">
                                                Loading image...
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )
                    })
                  ) : (
                    <div className="text-center py-8">
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        No Proof of Concept added yet.
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            {/* Footer */}
            <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={closeVulnModal}
                className="px-4 py-2 rounded-md text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Vulnerability Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={closeCreateModal}
          />
          <div className="relative z-10 w-full max-w-2xl mx-4 bg-white dark:bg-gray-800 rounded-lg shadow-lg">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Create New Vulnerability
              </h3>
              <button
                onClick={closeCreateModal}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                ✕
              </button>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200 dark:border-gray-700">
              <nav className="flex px-6 -mb-px space-x-8" aria-label="Tabs">
                <button
                  type="button"
                  onClick={() => setCreateTab('general')}
                  className={`whitespace-nowrap py-3 px-1 border-b-2 text-sm font-medium ${createTab === 'general'
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                    }`}
                >
                  General
                </button>
                <button
                  type="button"
                  onClick={() => setCreateTab('poc')}
                  className={`whitespace-nowrap py-3 px-1 border-b-2 text-sm font-medium ${createTab === 'poc'
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                    }`}
                >
                  POC
                </button>
              </nav>
            </div>

            {/* Form */}
            <form
              onSubmit={handleCreateVulnerability}
              className="px-6 py-4 max-h-[70vh] overflow-y-auto"
            >
              {/* General Tab */}
              {createTab === 'general' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Vulnerability Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                    placeholder="Enter vulnerability name"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      CVSS Score *
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        name="cvss"
                        value={formData.cvss}
                        onChange={handleInputChange}
                        min="0"
                        max="10"
                        step="0.1"
                        required
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                        placeholder="0.0 - 10.0"
                      />
                      {typeof formData.cvss === 'number' &&
                        formData.cvss > 0 && (
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getCvssSeverity(formData.cvss).color
                              }`}
                          >
                            {getCvssSeverity(formData.cvss).level}
                          </span>
                        )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      SLA (Days) *
                    </label>
                    <input
                      type="number"
                      name="sla"
                      value={formData.sla}
                      onChange={handleInputChange}
                      min="1"
                      required
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                      placeholder="30"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Synopsis *
                  </label>
                  <textarea
                    name="synopsis"
                    value={formData.synopsis}
                    onChange={handleInputChange}
                    required
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                    placeholder="Describe the vulnerability"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Impact *
                  </label>
                  <textarea
                    name="impact"
                    value={formData.impact}
                    onChange={handleInputChange}
                    required
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                    placeholder="Describe the potential impact"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Recommendation *
                  </label>
                  <textarea
                    name="recommendation"
                    value={formData.recommendation}
                    onChange={handleInputChange}
                    required
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                    placeholder="Provide remediation recommendations"
                  />
                </div>

                {/* References */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      References
                    </label>
                    <button
                      type="button"
                      onClick={addReference}
                      className="inline-flex items-center px-2 py-1 text-xs font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Add Reference
                    </button>
                  </div>
                  <div className="space-y-2">
                    {references.map((reference, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <input
                          type="url"
                          value={reference}
                          onChange={e => updateReference(index, e.target.value)}
                          placeholder="https://example.com/reference"
                          className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                        />
                        {references.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeReference(index)}
                            className="p-2 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              )}

              {/* POC Tab */}
              {createTab === 'poc' && (
                <div className="space-y-4">
                {/* Proof of Concept (POC) */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Proof of Concept (POC)
                    </label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => addPoc('TEXT')}
                        className="inline-flex items-center px-2 py-1 text-xs font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 border border-primary-300 dark:border-primary-600 rounded"
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        Add Text POC
                      </button>
                      <button
                        type="button"
                        onClick={() => addPoc('IMAGE')}
                        className="inline-flex items-center px-2 py-1 text-xs font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 border border-primary-300 dark:border-primary-600 rounded"
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        Add Image POC
                      </button>
                    </div>
                  </div>

                  <div className="space-y-4">
                    {pocs.map((poc, pocIndex) => (
                      <div key={pocIndex} className="border border-gray-300 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-700/50">
                        <div className="flex items-center justify-between mb-3">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200">
                            {poc.type === 'TEXT' ? 'Text POC' : 'Image POC'}
                          </span>
                          <button
                            type="button"
                            onClick={() => removePoc(pocIndex)}
                            className="p-1 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>

                        {/* TEXT POC */}
                        {poc.type === 'TEXT' && (
                          <div className="space-y-3">
                            {/* Caption */}
                            <div>
                              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Caption
                              </label>
                              <input
                                type="text"
                                value={poc.caption}
                                onChange={e => updatePocCaption(pocIndex, e.target.value)}
                                placeholder="Enter caption"
                                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                              />
                            </div>
                            
                            {/* Details */}
                            <div>
                              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Details
                              </label>
                              <textarea
                                value={poc.details}
                                onChange={e => updateTextPocDetails(pocIndex, e.target.value)}
                                placeholder="Enter detailed description (supports HTML)"
                                rows={4}
                                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white font-mono"
                              />
                              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                You can use HTML tags for formatting
                              </p>
                            </div>
                          </div>
                        )}

                        {/* IMAGE POC */}
                        {poc.type === 'IMAGE' && (
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">
                                Images
                              </label>
                              <button
                                type="button"
                                onClick={() => addImageToPoc(pocIndex)}
                                className="inline-flex items-center px-2 py-1 text-xs font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                              >
                                <Plus className="h-3 w-3 mr-1" />
                                Add Image
                              </button>
                            </div>
                            <div className="space-y-3">
                              {poc.images.map((image, imageIndex) => (
                                <div key={imageIndex} className="border border-gray-300 dark:border-gray-600 rounded p-3 bg-white dark:bg-gray-800">
                                  <div className="flex items-start justify-between mb-2">
                                    <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                                      Image {imageIndex + 1}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() => removeImageFromPoc(pocIndex, imageIndex)}
                                      className="p-1 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                                    >
                                      <X className="h-3 w-3" />
                                    </button>
                                  </div>
                                  <div className="space-y-2">
                                    <div>
                                      <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                                        Image Caption
                                      </label>
                                      <input
                                        type="text"
                                        value={image.caption}
                                        onChange={e => updateImageCaption(pocIndex, imageIndex, e.target.value)}
                                        placeholder="e.g., Step 1: Login page"
                                        className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                                        Upload Image
                                      </label>
                                      <input
                                        type="file"
                                        accept="image/*"
                                        onChange={e => {
                                          const file = e.target.files?.[0]
                                          if (file) {
                                            updateImageFile(pocIndex, imageIndex, file)
                                          }
                                        }}
                                        className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                                      />
                                      {image.file && (
                                        <p className="mt-1 text-xs text-green-600 dark:text-green-400">
                                          Selected: {image.file.name}
                                        </p>
                                      )}
                                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                        Image will be uploaded when vulnerability is saved
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              ))}
                              {poc.images.length === 0 && (
                                <p className="text-xs text-gray-500 dark:text-gray-400 text-center py-2">
                                  No images added yet. Click "Add Image" to add one.
                                </p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                    {pocs.length === 0 && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-3">
                        No POCs added yet. Click "Add Text POC" or "Add Image POC" to add one.
                      </p>
                    )}
                  </div>
                </div>
                </div>
              )}

              {/* Error display */}
              {error && (
                <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                  <p className="text-sm text-red-600 dark:text-red-400">
                    {error}
                  </p>
                </div>
              )}

              {/* Footer */}
              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={closeCreateModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Creating...' : 'Create Vulnerability'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Vulnerability Modal */}
      {showEditModal && editingVuln && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={closeEditModal}
          />
          <div className="relative z-10 w-full max-w-2xl mx-4 bg-white dark:bg-gray-800 rounded-lg shadow-lg">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Edit Vulnerability
              </h3>
              <button
                onClick={closeEditModal}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                ✕
              </button>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200 dark:border-gray-700">
              <nav className="flex px-6 -mb-px space-x-8" aria-label="Tabs">
                <button
                  type="button"
                  onClick={() => setEditTab('general')}
                  className={`whitespace-nowrap py-3 px-1 border-b-2 text-sm font-medium ${editTab === 'general'
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                    }`}
                >
                  General
                </button>
                <button
                  type="button"
                  onClick={() => setEditTab('poc')}
                  className={`whitespace-nowrap py-3 px-1 border-b-2 text-sm font-medium ${editTab === 'poc'
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                    }`}
                >
                  POC
                </button>
              </nav>
            </div>

            {/* Form */}
            <form
              onSubmit={handleUpdateVulnerability}
              className="px-6 py-4 max-h-[70vh] overflow-y-auto"
            >
              {/* General Tab */}
              {editTab === 'general' && (
              <div className="space-y-4">
                {/* Read-only Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Vulnerability Name
                  </label>
                  <input
                    type="text"
                    value={editingVuln.name}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      CVSS Score *
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        value={editFormData.cvss}
                        onChange={e =>
                          setEditFormData({
                            ...editFormData,
                            cvss:
                              e.target.value === ''
                                ? ''
                                : parseFloat(e.target.value),
                          })
                        }
                        min="0"
                        max="10"
                        step="0.1"
                        required
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                        placeholder="0.0 - 10.0"
                      />
                      {typeof editFormData.cvss === 'number' &&
                        editFormData.cvss > 0 && (
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getCvssSeverity(editFormData.cvss).color
                              }`}
                          >
                            {getCvssSeverity(editFormData.cvss).level}
                          </span>
                        )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      SLA (Days) *
                    </label>
                    <input
                      type="number"
                      value={editFormData.sla}
                      onChange={e =>
                        setEditFormData({
                          ...editFormData,
                          sla:
                            e.target.value === ''
                              ? ''
                              : parseFloat(e.target.value),
                        })
                      }
                      min="1"
                      required
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                      placeholder="30"
                    />
                  </div>
                </div>

                {/* Status Dropdown */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Status *
                  </label>
                  <select
                    value={editFormData.status}
                    onChange={e =>
                      setEditFormData({
                        ...editFormData,
                        status: e.target.value,
                      })
                    }
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                  >
                    <option value={Status.NOT_FIXED}>Not Fixed</option>
                    <option value={Status.FIXED}>Fixed</option>
                    <option value={Status.READY_TO_REVALIDATE}>
                      Ready to Revalidate
                    </option>
                    <option value={Status.OUT_OF_SCOPE}>Out of Scope</option>
                  </select>
                </div>

                {/* Read-only Synopsis */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Synopsis
                  </label>
                  <textarea
                    value={editingVuln.synopsis}
                    disabled
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Impact *
                  </label>
                  <textarea
                    value={editFormData.impact}
                    onChange={e =>
                      setEditFormData({
                        ...editFormData,
                        impact: e.target.value,
                      })
                    }
                    required
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                    placeholder="Describe the potential impact"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Recommendation *
                  </label>
                  <textarea
                    value={editFormData.recommendation}
                    onChange={e =>
                      setEditFormData({
                        ...editFormData,
                        recommendation: e.target.value,
                      })
                    }
                    required
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                    placeholder="Provide remediation recommendations"
                  />
                </div>

                {/* References */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      References
                    </label>
                    <button
                      type="button"
                      onClick={() => setEditReferences([...editReferences, ''])}
                      className="inline-flex items-center px-2 py-1 text-xs font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Add Reference
                    </button>
                  </div>
                  <div className="space-y-2">
                    {editReferences.map((reference, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <input
                          type="url"
                          value={reference}
                          onChange={e => {
                            const updated = [...editReferences]
                            updated[index] = e.target.value
                            setEditReferences(updated)
                          }}
                          placeholder="https://example.com/reference"
                          className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                        />
                        {editReferences.length > 1 && (
                          <button
                            type="button"
                            onClick={() => {
                              const updated = editReferences.filter(
                                (_, i) => i !== index
                              )
                              setEditReferences(
                                updated.length === 0 ? [''] : updated
                              )
                            }}
                            className="p-2 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              )}

              {/* POC Tab */}
              {editTab === 'poc' && (
                <div className="space-y-4">
                {/* Proof of Concept (POC) */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Proof of Concept (POC)
                    </label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => addEditPoc('TEXT')}
                        className="inline-flex items-center px-2 py-1 text-xs font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 border border-primary-300 dark:border-primary-600 rounded"
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        Add Text POC
                      </button>
                      <button
                        type="button"
                        onClick={() => addEditPoc('IMAGE')}
                        className="inline-flex items-center px-2 py-1 text-xs font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 border border-primary-300 dark:border-primary-600 rounded"
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        Add Image POC
                      </button>
                    </div>
                  </div>

                  <div className="space-y-4">
                    {editPocs.map((poc, pocIndex) => (
                      <div key={pocIndex} className="border border-gray-300 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-700/50">
                        <div className="flex items-center justify-between mb-3">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200">
                            {poc.type === 'TEXT' ? 'Text POC' : 'Image POC'}
                          </span>
                          <button
                            type="button"
                            onClick={() => removeEditPoc(pocIndex)}
                            className="p-1 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>

                        {/* TEXT POC */}
                        {poc.type === 'TEXT' && (
                          <div className="space-y-3">
                            {/* Caption */}
                            <div>
                              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Caption
                              </label>
                              <input
                                type="text"
                                value={poc.caption}
                                onChange={e => updateEditPocCaption(pocIndex, e.target.value)}
                                placeholder="Enter caption"
                                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                              />
                            </div>
                            
                            {/* Details */}
                            <div>
                              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Details
                              </label>
                              <textarea
                                value={poc.details}
                                onChange={e => updateEditTextPocDetails(pocIndex, e.target.value)}
                                placeholder="Enter detailed description (supports HTML)"
                                rows={4}
                                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white font-mono"
                              />
                              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                You can use HTML tags for formatting
                              </p>
                            </div>
                          </div>
                        )}

                        {/* IMAGE POC */}
                        {poc.type === 'IMAGE' && (
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">
                                Images
                              </label>
                              <button
                                type="button"
                                onClick={() => addImageToEditPoc(pocIndex)}
                                className="inline-flex items-center px-2 py-1 text-xs font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                              >
                                <Plus className="h-3 w-3 mr-1" />
                                Add Image
                              </button>
                            </div>
                            <div className="space-y-3">
                              {poc.images.map((image, imageIndex) => (
                                <div key={imageIndex} className="border border-gray-300 dark:border-gray-600 rounded p-3 bg-white dark:bg-gray-800">
                                  <div className="flex items-start justify-between mb-2">
                                    <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                                      Image {imageIndex + 1}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() => removeImageFromEditPoc(pocIndex, imageIndex)}
                                      className="p-1 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                                    >
                                      <X className="h-3 w-3" />
                                    </button>
                                  </div>
                                  <div className="space-y-2">
                                    <div>
                                      <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                                        Image Caption
                                      </label>
                                      <input
                                        type="text"
                                        value={image.caption}
                                        onChange={e => updateEditImageCaption(pocIndex, imageIndex, e.target.value)}
                                        placeholder="e.g., Step 1: Login page"
                                        className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                                        Upload Image
                                      </label>
                                      <input
                                        type="file"
                                        accept="image/*"
                                        onChange={e => {
                                          const file = e.target.files?.[0]
                                          if (file) {
                                            updateEditImageFile(pocIndex, imageIndex, file)
                                          }
                                        }}
                                        className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                                      />
                                      {image.file && (
                                        <p className="mt-1 text-xs text-green-600 dark:text-green-400">
                                          Selected: {image.file.name}
                                        </p>
                                      )}
                                      {image.imageKey && !image.file && (
                                        <p className="mt-1 text-xs text-blue-600 dark:text-blue-400">
                                          Current: {image.imageKey}
                                        </p>
                                      )}
                                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                        {image.file ? 'New image will be uploaded when saved' : 'Upload a new image to replace existing one'}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              ))}
                              {poc.images.length === 0 && (
                                <p className="text-xs text-gray-500 dark:text-gray-400 text-center py-2">
                                  No images added yet. Click "Add Image" to add one.
                                </p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                    {editPocs.length === 0 && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-3">
                        No POCs added yet. Click "Add Text POC" or "Add Image POC" to add one.
                      </p>
                    )}
                  </div>
                </div>
                </div>
              )}

              {/* Error display */}
              {error && (
                <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                  <p className="text-sm text-red-600 dark:text-red-400">
                    {error}
                  </p>
                </div>
              )}

              {/* Footer */}
              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={closeEditModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Updating...' : 'Update Vulnerability'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Import Vulnerability Modal */}
      {showImportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={closeImportModal}
          />
          <div className="relative z-10 w-full max-w-4xl mx-4 bg-white dark:bg-gray-800 rounded-lg shadow-lg">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Import Saved Vulnerability
              </h3>
              <button
                onClick={closeImportModal}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                ✕
              </button>
            </div>

            {/* Search Bar */}
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <input
                type="text"
                placeholder="Search vulnerabilities by name..."
                value={importSearchQuery}
                onChange={e => handleImportSearch(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
              />
            </div>

            {/* Content */}
            <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
              {importLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="text-gray-500 dark:text-gray-400">
                    Loading vulnerabilities...
                  </div>
                </div>
              ) : availableVulnerabilities.length === 0 ? (
                <div className="text-center py-8">
                  <div className="text-gray-500 dark:text-gray-400">
                    {importSearchQuery
                      ? 'No vulnerabilities found matching your search.'
                      : 'No saved vulnerabilities found.'}
                  </div>
                </div>
              ) : (
                <>
                  <div className="space-y-3">
                    {availableVulnerabilities
                      .slice((importPage - 1) * importPageSize, importPage * importPageSize)
                      .map(vulnerability => (
                        <div
                          key={vulnerability._id}
                          className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                          onClick={() => handleImportVulnerability(vulnerability)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                                {vulnerability.name}
                              </h4>
                              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                                {vulnerability.synopsis}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                  
                  {/* Pagination */}
                  <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-2">
                      <label className="text-sm text-gray-700 dark:text-gray-300">
                        Show:
                      </label>
                      <select
                        value={importPageSize}
                        onChange={(e) => {
                          setImportPageSize(Number(e.target.value))
                          setImportPage(1)
                        }}
                        className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                      </select>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        Showing {Math.min((importPage - 1) * importPageSize + 1, availableVulnerabilities.length)} to {Math.min(importPage * importPageSize, availableVulnerabilities.length)} of {availableVulnerabilities.length}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setImportPage(p => Math.max(1, p - 1))}
                        disabled={importPage === 1}
                        className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        Previous
                      </button>
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        Page {importPage} of {Math.ceil(availableVulnerabilities.length / importPageSize)}
                      </span>
                      <button
                        onClick={() => setImportPage(p => Math.min(Math.ceil(availableVulnerabilities.length / importPageSize), p + 1))}
                        disabled={importPage >= Math.ceil(availableVulnerabilities.length / importPageSize)}
                        className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={closeImportModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* History Modal */}
      {showHistoryModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={closeHistoryModal}
          />
          <div className="relative z-10 w-full max-w-3xl mx-4 bg-white dark:bg-gray-800 rounded-lg shadow-lg">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Scan History
              </h3>
              <button
                onClick={closeHistoryModal}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                ✕
              </button>
            </div>

            {/* Content */}
            <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
              {historyLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="text-gray-500 dark:text-gray-400">
                    Loading history...
                  </div>
                </div>
              ) : historyData.length === 0 ? (
                <div className="text-center py-8">
                  <div className="text-gray-500 dark:text-gray-400">
                    No history found for this scan.
                  </div>
                </div>
              ) : (
                <div className="relative">
                  {/* Stepper */}
                  <div className="space-y-6">
                    {historyData.map((item, index) => {
                      const isLast = index === historyData.length - 1
                      return (
                        <div key={item._id} className="relative flex gap-4">
                          {/* Timeline Line */}
                          {!isLast && (
                            <div className="absolute left-5 top-10 bottom-0 w-0.5 bg-gray-200 dark:bg-gray-700" />
                          )}
                          
                          {/* Timeline Dot */}
                          <div className="relative flex-shrink-0">
                            <div className="h-10 w-10 rounded-full bg-primary-600 dark:bg-primary-500 flex items-center justify-center">
                              <History className="h-5 w-5 text-white" />
                            </div>
                          </div>

                          {/* Content */}
                          <div className="flex-1 pb-6">
                            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                              <div className="flex items-start justify-between mb-2">
                                <h4 className="text-base font-semibold text-gray-900 dark:text-white">
                                  {getActionLabel(item.action)}
                                </h4>
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  {formatHistoryDate(item.createdAt)}
                                </span>
                              </div>

                              {/* User Info */}
                              {item.changedBy && (
                                <div className="flex items-center gap-2 mb-2">
                                  <div className="h-6 w-6 bg-primary-600 text-white rounded-full flex items-center justify-center text-xs font-semibold">
                                    {`${item.changedBy.firstName?.charAt(0) || ''}${item.changedBy.lastName?.charAt(0) || ''}`}
                                  </div>
                                  <span className="text-sm text-gray-600 dark:text-gray-300">
                                    {item.changedBy.firstName} {item.changedBy.lastName}
                                  </span>
                                  <span className="text-xs text-gray-500 dark:text-gray-400">
                                    ({item.changedBy.email})
                                  </span>
                                </div>
                              )}

                              {/* Additional Details */}
                              <div className="space-y-1 text-sm">
                                {item.reason && (
                                  <p className="text-gray-600 dark:text-gray-300">
                                    <span className="font-medium">Reason:</span> {item.reason}
                                  </p>
                                )}
                                {item.previousPublishedStatus && item.newPublishedStatus && (
                                  <p className="text-gray-600 dark:text-gray-300">
                                    <span className="font-medium">Status changed from:</span>{' '}
                                    <span className="px-2 py-0.5 rounded bg-gray-200 dark:bg-gray-600">
                                      {item.previousPublishedStatus}
                                    </span>
                                    {' → '}
                                    <span className="px-2 py-0.5 rounded bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
                                      {item.newPublishedStatus}
                                    </span>
                                  </p>
                                )}
                                {item.newStatus && (
                                  <p className="text-gray-600 dark:text-gray-300">
                                    <span className="font-medium">New Status:</span>{' '}
                                    <span className="px-2 py-0.5 rounded bg-primary-100 dark:bg-primary-900 text-primary-800 dark:text-primary-200">
                                      {item.newStatus.replace(/_/g, ' ')}
                                    </span>
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={closeHistoryModal}
                className="px-4 py-2 rounded-md text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ScanDetails
